import { Text } from '@thanx/uikit/text'
import type { MockOrder, MockOrderItem } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { currencyFormatter } from 'utilities/formatters'

const t = buildTranslate('recovery_ai.sandbox')

// The previous multi-row card's fill (grey05, 8px radius, 12px padding — see
// git history at 1355a4600) turned out to be invisible here: the wizard PAGE
// itself is painted grey05 (Wizard/index.tsx's root div), so a grey05 card on
// a grey05 page rendered as bare text with no visible boundary (confirmed via
// screenshot). White + a 1px grey20 border reads as a card against that same
// grey05 background — it's the exact chrome AgentSettingsCard and the
// non-wizard DecisionLogPanel already use elsewhere in this same right rail,
// so this keeps the sibling cards visually consistent rather than
// introducing a third treatment.
const CARD_PADDING_PX = 12
const CARD_RADIUS_PX = 8
// Space between the label line and the content line directly under it.
const CONTENT_MARGIN_TOP_PX = 4

type Props = {
  order: MockOrder
}

const formatCents = (cents: number): string => currencyFormatter(cents / 100)

// Exported so the geometry is unit-testable: styletron's css() is a no-op in the
// jsdom test env, so these rules can never be asserted through the rendered DOM.
//
// Both colors come from constants/palette.ts rather than mixing a raw 'white'
// keyword with a theme token (the sibling DecisionLogPanel.tsx does mix them —
// out of scope here — but there's no reason for this file to repeat that).
export const buildOrderDetailsCardStyle = (): StyleObject => ({
  backgroundColor: palette.white,
  border: `1px solid ${palette.grey20}`,
  borderRadius: `${CARD_RADIUS_PX}px`,
  padding: `${CARD_PADDING_PX}px`,
})

const contentStyle: StyleObject = {
  marginTop: `${CONTENT_MARGIN_TOP_PX}px`,
}

// `order_details_item_one`/`_other` are picked manually (react-redux-i18n does
// NOT auto-pluralize `%{count}` — see context/knowledge/i18n-pluralization.md)
// rather than passed a base key. Only the quantity prefix ("2 × ") is
// conditional; name/price are data and always present.
const buildItemSummary = (item: MockOrderItem): string => {
  const price = formatCents(item.price_cents)
  return item.quantity > 1
    ? t('order_details_item_other', {
        quantity: item.quantity,
        name: item.name,
        price,
      })
    : t('order_details_item_one', { name: item.name, price })
}

// The right rail's order-context card, rendered BETWEEN "Agent settings" and
// "AI reasoning" (SimulatorEngine.tsx) on every simulator surface — the wizard's
// guided AND free "Test your own" phases, and the standalone Agent Tuning tab.
// AI-162/ENG-971 dropped ScenarioDetailPanel from the wizard layout in favor of
// the centered scenario heading, which incidentally dropped order VISIBILITY
// there too — this restores it.
//
// Product feedback (post-launch, three rounds): (1) the original multi-row
// card (title, meta line, item rows, divider, total) ate too much vertical
// space, pushing the AI reasoning panel below the fold — collapsed to one
// wrapping sentence; (2) bare text in the rail read as "jammed" — restored the
// grey card chrome (now white + grey20 border — see buildOrderDetailsCardStyle
// for why plain grey05 was invisible on this grey05 page); (3) the single
// sentence was too compressed — split into a label line ("Current order
// details for this AI demo") and a content line (items + trailing delivery
// method), both inside the same card. Location/date/order-id are still
// intentionally dropped from the display (product's requested copy omits
// them).
//
// Presentational only: no fetch, no dispatch. Takes the bare `order` (not the
// `MockOrderScenario` wrapper — this never reads `dossier` or the scenario
// name/description) so the same value SimulatorEngine derives for
// useSimulatorData's `order_context` (`scenario?.order ?? getFreeChatOrder()`,
// see freeChatOrder.ts) is exactly what renders here — this can never show
// an order the agent isn't actually reasoning over.
const OrderDetailsCard = ({ order }: Props) => {
  const [css] = useStyletron()

  // Item list is data (names/prices), joined in code — only the sentence
  // shells (label + content) and each item's own phrasing go through i18n.
  const items = order.items.map(buildItemSummary).join(', ')
  const contentKey =
    order.delivery_method === 'delivery'
      ? 'order_details_content_delivery'
      : 'order_details_content_pickup'

  return (
    <div className={css(buildOrderDetailsCardStyle())}>
      <Text.BodyText5 tag="div" bold color="grey90">
        {t('order_details_label')}
      </Text.BodyText5>
      <Text.BodyText5 tag="div" color="grey70" className={css(contentStyle)}>
        {t(contentKey, { items })}
      </Text.BodyText5>
    </div>
  )
}

export default OrderDetailsCard
